/**
 * Client Data Service
 * Centralized service for building URLs and fetching client data files
 * from the remote uploads API with Bearer token authentication.
 *
 * Features:
 * - In-memory cache: files are fetched once and reused across route/component changes
 * - Deduplication: concurrent requests for the same file share a single network call
 * - Blob URL management: image/binary blob URLs are cached and never revoked while in cache
 *
 * API pattern: {UPLOADS_BASE_URL}/api/browse/{PROJECT_NAME}/{CLIENT_TYPE}/{FILE_PATH}
 * Auth: Bearer token in Authorization header
 *
 * Usage:
 *   import { getClientFileUrl, fetchClientJSON, getAuthHeaders } from '../services/clientDataService';
 *   const url = getClientFileUrl('lavagna', 'signal_fix/Car_gps.geojson');
 *   const data = await fetchClientJSON('lavagna', 'metrics.json');
 */

import { getAuthHeaders as sessionAuthHeaders, getAuthToken } from '../tools/session/clientSession';
import { getClientFileUrl } from '../tools/paths/clientPaths';
import { emitLoadingEnd, emitLoadingProgress, emitLoadingStart } from '../common/loading/loadingEvents';

// In-memory caches keyed by "{clientType}/{filePath}"
const jsonCache = new Map();
const textCache = new Map();
const blobUrlCache = new Map();
// In-flight promises to deduplicate concurrent requests
const inflightRequests = new Map();

/**
 * Cache API constants
 */
const CACHE_NAME_PREFIX = 'traffix-v1-';
const getCacheName = (clientType) => `${CACHE_NAME_PREFIX}${clientType}`;


const getCacheKey = (clientType, filePath, project) => {
  const cleanPath = filePath.startsWith('/') ? filePath.slice(1) : filePath;
  return project ? `${project}:${clientType}/${cleanPath}` : `${clientType}/${cleanPath}`;
};

/**
 * Get authorization headers for fetch requests
 * @returns {object} Headers object with Bearer token
 */
export const getAuthHeaders = () => sessionAuthHeaders();

/**
 * Build a full URL for a client data file on the remote API (without auth)
 * @param {string} clientType - The client identifier (e.g., 'lavagna', 'saronno')
 * @param {string} filePath - Relative path to the file (e.g., 'signal_fix/Car_gps.geojson')
 * @returns {string} Full API URL
 */
export { getClientFileUrl };

/**
 * Fetch a client data file with Bearer auth and return the Response object.
 * Implements a ReadableStream to dispatch robust global download progress percentages.
 * @param {string} clientType - The client identifier
 * @param {string} filePath - Relative path to the file
 * @returns {Promise<Response>} Fetch response built from the custom stream
 */
export const fetchClientFile = async (clientType, filePath, { trackLoading = true, project } = {}) => {
  const url = getClientFileUrl(clientType, filePath, project);
  
  // Dispatch start event so UI knows file is queued
  if (trackLoading) emitLoadingStart(filePath);

  try {
    const response = await fetch(url, { headers: getAuthHeaders() });
    
    if (!response.ok) {
      throw new Error(`Failed to fetch ${filePath}: HTTP ${response.status}`);
    }

    const contentLength = response.headers.get('content-length');
    // If no content limit returned, or body not readable, fallback elegantly
    if (!contentLength || !response.body) {
      if (trackLoading) emitLoadingEnd(filePath);
      return response;
    }

    const total = parseInt(contentLength, 10);
    let loaded = 0;
    const reader = response.body.getReader();

    const stream = new ReadableStream({
      start(controller) {
        function push() {
          reader.read().then(({ done, value }) => {
            if (done) {
              if (trackLoading) emitLoadingEnd(filePath);
              controller.close();
              return;
            }
            loaded += value.byteLength;
            const percentage = Math.round((loaded / total) * 100);
            if (trackLoading) emitLoadingProgress(filePath, percentage);
            
            controller.enqueue(value);
            push();
          }).catch(err => {
            console.error('Stream reading error:', err);
            controller.error(err);
            if (trackLoading) emitLoadingEnd(filePath);
          });
        }
        push();
      }
    });

    return new Response(stream, { headers: response.headers });
  } catch (error) {
    if (trackLoading) emitLoadingEnd(filePath);
    throw error;
  }
};

/**
 * Internal helper: deduplicate concurrent requests for the same cache key + type.
 * If a request for the same key is already in-flight, return its promise instead of starting a new one.
 */
const deduplicatedFetch = (cacheKey, type, fetchFn) => {
  const inflightKey = `${type}:${cacheKey}`;
  if (inflightRequests.has(inflightKey)) {
    return inflightRequests.get(inflightKey);
  }
  const promise = fetchFn().finally(() => {
    inflightRequests.delete(inflightKey);
  });
  inflightRequests.set(inflightKey, promise);
  return promise;
};

/**
 * Fetch a client data file as JSON (cached with persistent logic)
 * @param {string} clientType - The client identifier
 * @param {string} filePath - Relative path to the file
 * @returns {Promise<object>} Parsed JSON data
 */
export const fetchClientJSON = async (clientType, filePath, options = {}) => {
  const key = getCacheKey(clientType, filePath, options.project);
  if (jsonCache.has(key)) return jsonCache.get(key);
  const url = getClientFileUrl(clientType, filePath, options.project);

  return deduplicatedFetch(key, 'json', async () => {
    // 1. Check Browser Cache API (Persistent)
    try {
      if ('caches' in window) {
        const cache = await caches.open(getCacheName(clientType));
        // Include partial token in cache key if desired, or just use URL if token only controls access
        const cacheRequest = new Request(url, { headers: getAuthHeaders() });
        const cachedResponse = await cache.match(cacheRequest);
        
        if (cachedResponse) {
          console.log(`[Cache] Loading ${filePath} from persistent cache`);
          const data = await cachedResponse.json();
          jsonCache.set(key, data);
          return data;
        }
      }
    } catch (cacheError) {
      console.warn('Persistent cache match failed:', cacheError);
    }

    // 2. Fetch from Network if not in cache
    const response = await fetchClientFile(clientType, filePath, options);
    
    // Clone response before consuming for json() so we can also cache it
    const responseToCache = response.clone();
    const data = await response.json();
    
    // 3. Store in caches
    jsonCache.set(key, data);
    try {
      if ('caches' in window) {
        const cache = await caches.open(getCacheName(clientType));
        const cacheRequest = new Request(url, { headers: getAuthHeaders() });
        await cache.put(cacheRequest, responseToCache);
      }
    } catch (saveError) {
      console.warn('Failed to save to persistent cache:', saveError);
    }

    return data;
  });
};


/**
 * Fetch a client data file as text (cached, for CSV files)
 * @param {string} clientType - The client identifier
 * @param {string} filePath - Relative path to the file
 * @returns {Promise<string>} Text content
 */
export const fetchClientText = async (clientType, filePath, options = {}) => {
  const key = getCacheKey(clientType, filePath);
  if (textCache.has(key)) return textCache.get(key);

  return deduplicatedFetch(key, 'text', async () => {
    // 1. Check Browser Cache API (Persistent)
    try {
      if ('caches' in window) {
        const cache = await caches.open(getCacheName(clientType));
        const url = getClientFileUrl(clientType, filePath);
        const cacheRequest = new Request(url, { headers: getAuthHeaders() });
        const cachedResponse = await cache.match(cacheRequest);
        
        if (cachedResponse) {
          console.log(`[Cache] Loading ${filePath} from persistent cache`);
          const text = await cachedResponse.text();
          textCache.set(key, text);
          return text;
        }
      }
    } catch (cacheError) {
      console.warn('Persistent cache match failed:', cacheError);
    }

    // 2. Fetch from Network
    const response = await fetchClientFile(clientType, filePath, options);
    const responseToCache = response.clone();
    const text = await response.text();
    
    // 3. Store in caches
    textCache.set(key, text);
    try {
      if ('caches' in window) {
        const cache = await caches.open(getCacheName(clientType));
        const url = getClientFileUrl(clientType, filePath);
        const cacheRequest = new Request(url, { headers: getAuthHeaders() });
        await cache.put(cacheRequest, responseToCache);
      }
    } catch (saveError) {
      console.warn('Failed to save to persistent cache:', saveError);
    }

    return text;
  });
};


/**
 * Fetch a client data file as a blob URL (cached, for images, videos)
 * Use this for <img src>, <video src> etc. where headers can't be set directly.
 * Blob URLs are cached and stable — safe to use directly as src without revoking.
 * @param {string} clientType - The client identifier
 * @param {string} filePath - Relative path to the file
 * @returns {Promise<string>} Object URL for the blob
 */
export const fetchClientBlobUrl = async (clientType, filePath) => {
  const key = getCacheKey(clientType, filePath);
  if (blobUrlCache.has(key)) return blobUrlCache.get(key);

  return deduplicatedFetch(key, 'blob', async () => {
    const response = await fetchClientFile(clientType, filePath);
    const blob = await response.blob();
    const blobUrl = URL.createObjectURL(blob);
    blobUrlCache.set(key, blobUrl);
    return blobUrl;
  });
};

/**
 * Clear all caches (e.g., on logout or client switch).
 * Revokes all cached blob URLs to free memory and deletes persistent cache.
 */
export const clearClientDataCache = async () => {
  jsonCache.clear();
  textCache.clear();
  blobUrlCache.forEach(url => URL.revokeObjectURL(url));
  blobUrlCache.clear();

  // Clear persistent caches
  try {
    if ('caches' in window) {
      const keys = await caches.keys();
      await Promise.all(
        keys.filter(key => key.startsWith(CACHE_NAME_PREFIX))
            .map(key => caches.delete(key))
      );
    }
  } catch (error) {
    console.error('Failed to clear persistent caches:', error);
  }
};


export default {
  getAuthToken,
  getAuthHeaders,
  getClientFileUrl,
  fetchClientFile,
  fetchClientJSON,
  fetchClientText,
  fetchClientBlobUrl,
  clearClientDataCache,
};
